import React, { FC, useMemo } from 'react';
import { Box, ButtonV2, Flex, Menu, TextV2, useDisclosure } from '@withjoy/joykit';
import { animationTransition } from '@shared/utils/animationTransition';
import { useTranslation } from '@shared/core';
import { FullWidthImage, CenteredImage, PageWidthImage } from '@assets/icons';
import { useFeatureValue } from '@shared/core/featureFlags';
import { pxToRem } from '@withjoy/joykit/theme';
import { useOuterClick } from '@shared/utils/hooks/useOuterClick';
import { ChevronDown } from '@withjoy/joykit/icons';
import { SinglePhotoPresentationLayout } from '@shared/utils/photoPresentationLayout';

interface PhotoPresentationLayoutSelectorProps {
  currentPhotoLayout: SinglePhotoPresentationLayout;
  onPhotoLayoutChange: (layout: SinglePhotoPresentationLayout) => void;
}

interface LayoutOptionProps {
  isActive: boolean;
  onClick: (layout: SinglePhotoPresentationLayout) => void;
  value: SinglePhotoPresentationLayout;
  label: string;
  icon: React.ReactNode;
}

type MenuItemProps = {
  title: string;
  icon: React.ReactNode;
  onClick: (layout: SinglePhotoPresentationLayout) => void;
  value: SinglePhotoPresentationLayout;
};

type PhotoPresentationLayoutData = {
  [key in SinglePhotoPresentationLayout]: {
    title: string;
    icon: React.ReactNode;
  };
};

const LayoutOption = (props: LayoutOptionProps) => {
  const { isActive, label, value, onClick, icon } = props;
  return (
    <ButtonV2
      flex={1}
      padding={'10px'}
      intent="neutral"
      height="unset"
      borderWidth="1px"
      borderColor={isActive ? 'mono14' : 'mono5'}
      backgroundColor={isActive ? 'white' : 'mono2'}
      variant="outline"
      onClick={() => onClick(value)}
    >
      <Flex
        flexDirection="column"
        gap={3}
        justifyContent="center"
        alignItems="center"
      >
        {icon}
        <TextV2 typographyVariant="button2">{label}</TextV2>
      </Flex>
    </ButtonV2>
  );
};

const MenuItem: FC<MenuItemProps> = props => {
  const { title, icon, onClick, value } = props;
  return (
    <Menu.Item
      overrides={{
        ListItem: {
          props: {
            paddingX: 5,
            paddingY: 6,
            _hover: {
              backgroundColor: 'mono2',
            },
          },
        },
      }}
      content={
        <Flex
          flexDirection="row"
          gap={3}
          alignItems="center"
        >
          {icon}
          <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="flex-start"
          >
            <TextV2
              typographyVariant="button2"
              color="mono14"
            >
              {title}
            </TextV2>
          </Flex>
        </Flex>
      }
      onClick={() => onClick(value)}
    />
  );
};

export const PhotoPresentationLayoutSelector = (props: PhotoPresentationLayoutSelectorProps) => {
  const { currentPhotoLayout, onPhotoLayoutChange } = props;

  const { isOpen, onClose, onToggle: toggleMenu } = useDisclosure();
  const menuRef = useOuterClick(toggleMenu, isOpen);

  const isPageWidthPhotoEnabled = useFeatureValue('pageWidthPhotoEnabled').value === 'on';

  const { t } = useTranslation('joykit');
  const focusPointTransFn = t('focusPointDialog');

  const onMenuItemClick = (layout: SinglePhotoPresentationLayout) => {
    onPhotoLayoutChange(layout);
    onClose();
  };

  const photoPresentationLayoutData: PhotoPresentationLayoutData = useMemo(() => {
    return {
      fullWidth: {
        title: focusPointTransFn.photoLayoutDropDown.fullWidth.title(),
        icon: <FullWidthImage />,
      },
      pageWidth: {
        title: focusPointTransFn.photoLayoutDropDown.pageWidth.title(),
        icon: <PageWidthImage />,
      },
      center: {
        title: focusPointTransFn.photoLayoutDropDown.centered.title(),
        icon: <CenteredImage />,
      },
    };
  }, [focusPointTransFn.photoLayoutDropDown.centered, focusPointTransFn.photoLayoutDropDown.fullWidth, focusPointTransFn.photoLayoutDropDown.pageWidth]);

  return (
    <Flex
      flexDirection="column"
      marginTop={7}
      rowGap={5}
    >
      <TextV2 typographyVariant="hed2">{focusPointTransFn.photoLayout()}</TextV2>
      <Flex columnGap={5}>
        {isPageWidthPhotoEnabled ? (
          <Box
            position="relative"
            ref={menuRef}
            width="100%"
          >
            <Flex
              width="100%"
              height={pxToRem(100)}
              padding={pxToRem(10)}
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              border="2px solid"
              borderColor="mono14"
              borderRadius={2}
              onClick={toggleMenu}
              cursor="pointer"
            >
              <Flex
                width="100%"
                paddingLeft={5}
                paddingRight={3}
                justifyContent="space-between"
                alignItems="center"
              >
                <Flex
                  flexDirection="row"
                  gap={3}
                  alignItems="center"
                >
                  {photoPresentationLayoutData[currentPhotoLayout].icon}
                  <Flex
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="flex-start"
                  >
                    <TextV2
                      typographyVariant="button2"
                      color="mono14"
                    >
                      {photoPresentationLayoutData[currentPhotoLayout].title}
                    </TextV2>
                  </Flex>
                </Flex>
                <ChevronDown
                  size={16}
                  transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                />
              </Flex>
            </Flex>

            <Box
              width="100%"
              __css={{
                opacity: isOpen ? 1 : 0,
                transition: animationTransition('all'),
                transitionDuration: '0.3s',
                transformOrigin: 'right top',
                transform: isOpen ? 'scaleY(1) scaleX(1)' : 'scaleY(0.9) scaleX(0.9)',
                visibility: isOpen ? 'visible' : 'hidden',
                position: 'absolute',
                right: 0,
                zIndex: 'dropdown',
                marginTop: 2,
              }}
            >
              <Menu
                overrides={{
                  List: {
                    props: {
                      width: '100%',
                      maxWidth: 'none',
                      border: '2px solid',
                      borderColor: 'mono14',
                      padding: pxToRem(10),
                    },
                  },
                }}
              >
                <MenuItem
                  title={photoPresentationLayoutData.fullWidth.title}
                  icon={photoPresentationLayoutData.fullWidth.icon}
                  onClick={onMenuItemClick}
                  value="fullWidth"
                />
                <MenuItem
                  title={photoPresentationLayoutData.pageWidth.title}
                  icon={photoPresentationLayoutData.pageWidth.icon}
                  onClick={onMenuItemClick}
                  value="pageWidth"
                />
                <MenuItem
                  title={photoPresentationLayoutData.center.title}
                  icon={photoPresentationLayoutData.center.icon}
                  onClick={onMenuItemClick}
                  value="center"
                />
              </Menu>
            </Box>
          </Box>
        ) : (
          <>
            <LayoutOption
              isActive={currentPhotoLayout === 'fullWidth'}
              label={focusPointTransFn.fullWidthPhoto()}
              onClick={onPhotoLayoutChange}
              value="fullWidth"
              icon={<FullWidthImage />}
            />
            <LayoutOption
              isActive={currentPhotoLayout === 'center'}
              label={focusPointTransFn.centeredPhoto()}
              onClick={onPhotoLayoutChange}
              value="center"
              icon={<CenteredImage />}
            />
          </>
        )}
      </Flex>
    </Flex>
  );
};
